import {
  BusinessCalendar,
  BusinessCalendarDayWorkHours,
  CalendarInvalidReservationDates,
  CalendarType,
  CompanyCalendar,
  CompanyCalendarContent,
  CompanyCalendarDetails,
  CompanyCalendarSettings,
  CompanyCalendarWorkHours,
  WorkHoursWithInvalidReservationDates,
} from '../types/calendars';
import { handleResponse } from './handleResponse';
import apiAxios from './apiAxios';
import {
  CalendarServiceRelation,
  CalendarServiceRelationExisting,
  DeleteCalendarService,
  Image,
} from '../types/common';
import { CompanyStateChange } from '@src/@data/types/company';
import { PortalReservationBasicData } from '@src/@data/types/reservations';
import { convertToApiDate } from '@src/@data/utility/dates';

const calendarApi = {
  async listCompanyCalendars(companyId: string): Promise<CompanyCalendar[]> {
    return apiAxios
      .get(`/api/portal/company/${companyId}/calendars`)
      .then(handleResponse);
  },
  async getCompanyCalendar(
    companyId: string,
    calendarId: string
  ): Promise<CompanyCalendar> {
    return apiAxios
      .get(`/api/portal/company/${companyId}/calendars/${calendarId}`)
      .then(handleResponse);
  },
  async getCalendarServices(
    companyId: string,
    calendarId: string
  ): Promise<CalendarServiceRelationExisting[]> {
    return apiAxios
      .get(`/api/portal/company/${companyId}/calendars/${calendarId}/services`)
      .then(handleResponse);
  },
  async getCompanyCalendarContent(
    companyId: string,
    calendarId: string
  ): Promise<CompanyCalendarContent> {
    return apiAxios
      .get(`/api/portal/company/${companyId}/calendars/${calendarId}/content`)
      .then(handleResponse);
  },
  async getCompanyCalendarInvalidReservationDates(
    companyId: string,
    calendarId: string
  ): Promise<CalendarInvalidReservationDates> {
    return apiAxios
      .get(
        `/api/portal/company/${companyId}/calendars/${calendarId}/invalidReservationDates`
      )
      .then(handleResponse);
  },
  async updateCalendarEnabled(
    companyId: string,
    calendarId: string,
    enabled: boolean
  ): Promise<CompanyStateChange> {
    return apiAxios
      .put(
        `/api/portal/company/${companyId}/calendars/${calendarId}/edit/enable`,
        {
          enabled,
        }
      )
      .then(handleResponse);
  },
  async updateImage(
    companyId: string,
    calendarId: string,
    imageID: string
  ): Promise<Image> {
    return apiAxios
      .put(
        `/api/portal/company/${companyId}/calendars/${calendarId}/edit/image`,
        {
          imageID,
        }
      )
      .then(handleResponse);
  },
  async updateDetails(
    companyId: string,
    calendarId: string,
    details: CompanyCalendarDetails
  ): Promise<CompanyCalendarDetails> {
    return apiAxios
      .put(
        `/api/portal/company/${companyId}/calendars/${calendarId}/edit/details`,
        details
      )
      .then(handleResponse);
  },
  async updateWorkHours(
    companyId: string,
    calendarId: string,
    workHours: CompanyCalendarWorkHours
  ): Promise<WorkHoursWithInvalidReservationDates> {
    return apiAxios
      .put(
        `/api/portal/company/${companyId}/calendars/${calendarId}/edit/workHours`,
        workHours
      )
      .then(handleResponse);
  },
  async updateSettings(
    companyId: string,
    calendarId: string,
    settings: {
      autoConfirm: boolean;
      minStartDelay: number;
      step: number;
      stripeEnabled: boolean;
      daysToGenerate: number;
    }
  ): Promise<CompanyCalendarSettings> {
    return apiAxios
      .put(
        `/api/portal/company/${companyId}/calendars/${calendarId}/edit/settings`,
        settings
      )
      .then(handleResponse);
  },
  async updateCalendarServices(
    companyId: string,
    calendarId: string,
    setCalendarServices: CalendarServiceRelation[],
    deleteCalendarServices: DeleteCalendarService[]
  ): Promise<CompanyCalendarSettings> {
    return apiAxios.post(
      `/api/portal/company/${companyId}/calendars/${calendarId}/services`,
      {
        calendarServices: setCalendarServices,
        disabled: deleteCalendarServices,
      }
    );
  },
  async createCalendar(
    companyId: string,
    details: { title: string; type: CalendarType; services?: string[] }
  ): Promise<CompanyCalendar> {
    return apiAxios
      .post(`/api/portal/company/${companyId}/calendars`, details)
      .then(handleResponse);
  },
  async deleteCalendar(
    companyId: string,
    calendarId: string
  ): Promise<CompanyStateChange> {
    return apiAxios
      .delete(`/api/portal/company/${companyId}/calendars/${calendarId}`)
      .then(handleResponse);
  },
  async getBusinessCalendars(
    companyId: string,
    type?: CalendarType
  ): Promise<BusinessCalendar[]> {
    return apiAxios
      .get(`/api/companies/${companyId}/calendars`, { params: { type } })
      .then(handleResponse);
  },
  async cloneCalendar(
    companyId: string,
    calendarId: string
  ): Promise<CompanyCalendar> {
    return apiAxios
      .post(`/api/portal/company/${companyId}/calendars/${calendarId}/clone`)
      .then(handleResponse);
  },
  async getCalendarDayWorkHours(
    companyId: string,
    calendarId: string,
    date: string
  ): Promise<BusinessCalendarDayWorkHours> {
    return apiAxios
      .get(`/api/companies/${companyId}/calendars/${calendarId}/hours`, {
        params: { date },
      })
      .then(handleResponse);
  },
  async changeCalendarDayWorkHours(
    companyId: string,
    calendarId: string,
    data: BusinessCalendarDayWorkHours
  ): Promise<void> {
    return apiAxios
      .post(`/api/companies/${companyId}/calendars/${calendarId}/hours`, data)
      .then(handleResponse);
  },
  async getReservations(
    companyId: string,
    calendarId: string,
    from: Date,
    to: Date
  ): Promise<PortalReservationBasicData[]> {
    return apiAxios
      .get(
        `/api/portal/company/${companyId}/calendars/${calendarId}/reservations`,
        {
          params: {
            startDate: convertToApiDate(from),
            endDate: convertToApiDate(to),
          },
        }
      )
      .then(handleResponse);
  },
};

export default calendarApi;
